import { ReactNode } from "react";
import { YStack, XStack, Text, Spinner } from "tamagui";
import { colors } from "@styles/colors";
import { v2 } from "@screen/Home/pages/GasShop/skin";

/**
 * 「剛印出來的收據」。下單成功頁與付費明細共用。
 *
 * 版面：出紙口（膠囊橫桿）咬住紙的上緣 → 白紙（紙頭有一段灰白的背光漸層）→
 * 金額主秀 → 打孔虛線（兩側半圓缺口）→ 「標籤：值」的明細 → 撕齒下緣。
 * 樣式全在 all.scss 的 .gas-receipt-* / .gas-printer-bar，這裡只組結構。
 *
 * 這幾層裝飾一律用原生 div：Tamagui 的 YStack 會帶自己的 class（position: relative、
 * 背景等），把 SCSS 裡的 position: absolute 與 background-image 蓋掉——出紙口會掉到紙的
 * 最底下、撕齒整個不見。
 */
export interface ReceiptRow {
  k: string;
  v: string;
}

interface ReceiptPaperProps {
  /** 金額。null＝不揭露（如待確認前隱藏金額），改用 fallbackTitle 當主秀。 */
  amount?: number | null;
  /** 沒有金額時放在主秀位置的字（如單號、「待確認」）。 */
  fallbackTitle?: string;
  rows: ReceiptRow[];
  /** 明細還在載入 */
  loading?: boolean;
  /** 撕線下方的額外內容（如狀態說明） */
  footer?: ReactNode;
}

const ReceiptPaper = ({ amount, fallbackTitle, rows, loading = false, footer }: ReceiptPaperProps) => (
  // 紙離螢幕左右各 14px：參考稿的紙幾乎滿版，只露出一線色帶當作紙的邊界
  <div style={{ position: "relative", marginTop: 18, marginLeft: 14, marginRight: 14 }}>
    <div className="gas-receipt-slot">
      <div className="gas-receipt-paper">
        {/* 背景（紙頭的灰白漸層）定義在 all.scss 的 .gas-receipt-head */}
        <div className="gas-receipt-head" style={{ padding: "26px 22px 26px" }}>
          {loading ? (
            <YStack ai="center" jc="center" py={30}>
              <Spinner color={colors.primary} />
            </YStack>
          ) : (
            <>
              {amount != null ? (
                <XStack ai="baseline" jc="center" gap={2}>
                  <Text fontSize={16} fontWeight="700" color={v2.title}>
                    NT$
                  </Text>
                  <Text
                    fontSize={34}
                    fontWeight="800"
                    color={v2.title}
                    letterSpacing={-1}
                    style={{ fontVariantNumeric: "tabular-nums" }}>
                    {amount.toLocaleString()}
                  </Text>
                </XStack>
              ) : (
                <Text fontSize={19} fontWeight="800" color={v2.title} textAlign="center">
                  {fallbackTitle || "—"}
                </Text>
              )}

              {/* 打孔撕線：兩側半圓缺口 + 中間虛線 */}
              <div className="gas-receipt-perf" style={{ margin: "20px 0 22px" }} />

              <YStack gap={16}>
                {rows.map((r) => (
                  <XStack key={r.k}>
                    <Text fontSize={13.5} color={v2.sub} flexShrink={0}>
                      {r.k}：
                    </Text>
                    <Text
                      fontSize={13.5}
                      color={v2.body}
                      numberOfLines={1}
                      flexShrink={1}
                      style={{ fontVariantNumeric: "tabular-nums" }}>
                      {r.v}
                    </Text>
                  </XStack>
                ))}
              </YStack>
              {footer}
            </>
          )}
        </div>
        {/* 撕齒下緣 */}
        <div className="gas-receipt-teeth" />
      </div>
    </div>
    {/* 出紙口，壓在紙上緣 */}
    <div className="gas-printer-bar" />
  </div>
);

export default ReceiptPaper;
